import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '../../context/ThemeContext';

export interface EmptyStateProps {
  /** Icon element (e.g. HeroIcon or emoji string) */
  icon: React.ReactNode;
  /** Main heading */
  title: string;
  /** Optional subtitle / helper text */
  subtitle?: string;
  /** Optional action button (e.g. PrimaryButton) */
  actionButton?: React.ReactNode;
  /**
   * `decorative` — light tint background + colored icon (default).
   * `actionable` — solid background + white text, draws attention to CTA.
   */
  variant?: 'decorative' | 'actionable';
  /** data-testid forwarded to root element */
  'data-testid'?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  subtitle,
  actionButton,
  variant = 'decorative',
  'data-testid': testId = 'empty-state',
}) => {
  const { theme } = useTheme();

  const isActionable = variant === 'actionable';

  return (
    <Box
      data-testid={testId}
      sx={{
        py: 6,
        px: 3,
        textAlign: 'center',
        borderRadius: '16px',
        backgroundColor: isActionable ? theme.primary : theme.gray[100],
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          mb: 1.5,
          fontSize: '2.5rem',
          lineHeight: 1,
          color: isActionable ? '#fff' : theme.primary,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '& svg': { width: 40, height: 40 },
        }}
      >
        {icon}
      </Box>

      {/* Title */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          color: isActionable ? '#fff' : theme.text,
          mb: subtitle ? 0.5 : actionButton ? 2 : 0,
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      {subtitle && (
        <Typography
          variant="body2"
          sx={{
            color: isActionable ? 'rgba(255,255,255,0.85)' : theme.gray[500],
            mb: actionButton ? 2 : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* Action */}
      {actionButton && <Box>{actionButton}</Box>}
    </Box>
  );
};

export default EmptyState;
